'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, Bell, BellOff, X, ChevronDown, ChevronUp, Clock, Activity, Brain, CheckCircle } from 'lucide-react';
import { useSession } from './SessionProvider';

// ─── Threshold Definitions ────────────────────────────────────────────────────
interface BiomarkerThreshold {
  id: string;
  name: string;
  condition: string;
  category: 'alzheimer' | 'adhd' | 'signal';
  severity: 'warning' | 'critical';
  actionRequired: string;
  clinicalNote: string;
}

const THRESHOLDS: BiomarkerThreshold[] = [
  // Alzheimer biomarkers
  {
    id: 'alz-tar-critical',
    name: 'Theta/Alpha Ratio',
    condition: 'TAR > 2.2',
    category: 'alzheimer',
    severity: 'critical',
    actionRequired: 'Initiate Alzheimer protocol — specialist referral required',
    clinicalNote: 'Severe cortical slowing pattern. TAR > 2.2 indicates significant neurodegeneration risk.',
  },
  {
    id: 'alz-tar-warning',
    name: 'Theta/Alpha Ratio',
    condition: 'TAR > 1.8',
    category: 'alzheimer',
    severity: 'warning',
    actionRequired: 'Schedule cognitive assessment within 48h',
    clinicalNote: 'Elevated theta slowing. Monitor for progression. Document in patient record.',
  },
  {
    id: 'alz-coherence-critical',
    name: 'Inter-Hemispheric Coherence',
    condition: 'Coherence < 52%',
    category: 'alzheimer',
    severity: 'critical',
    actionRequired: 'Urgent: Review connectivity deficit — consult neurologist',
    clinicalNote: 'Critically low inter-hemispheric coherence. Associated with advanced cortical disconnection.',
  },
  {
    id: 'alz-coherence-warning',
    name: 'Inter-Hemispheric Coherence',
    condition: 'Coherence < 60%',
    category: 'alzheimer',
    severity: 'warning',
    actionRequired: 'Flag for clinical review — coherence trending low',
    clinicalNote: 'Below-normal coherence. May indicate early disconnection syndrome.',
  },
  {
    id: 'alz-delta-critical',
    name: 'Delta Power',
    condition: 'Delta > 22 µV²',
    category: 'alzheimer',
    severity: 'critical',
    actionRequired: 'Assess consciousness level — abnormal slow-wave activity',
    clinicalNote: 'Excessive delta in waking state. Rule out encephalopathy or deep sleep intrusion.',
  },
  // ADHD biomarkers
  {
    id: 'adhd-tbr-critical',
    name: 'Theta/Beta Ratio',
    condition: 'TBR > 2.4',
    category: 'adhd',
    severity: 'critical',
    actionRequired: 'Initiate ADHD intervention — neurofeedback session recommended',
    clinicalNote: 'TBR > 2.4 is strongly associated with severe attention dysregulation in clinical literature.',
  },
  {
    id: 'adhd-tbr-warning',
    name: 'Theta/Beta Ratio',
    condition: 'TBR > 1.8',
    category: 'adhd',
    severity: 'warning',
    actionRequired: 'Reduce task demands — attention regulation impaired',
    clinicalNote: 'Elevated TBR. Patient may struggle with sustained attention. Consider structured breaks.',
  },
  {
    id: 'adhd-attention-critical',
    name: 'Attention Score',
    condition: 'Attention < 30',
    category: 'adhd',
    severity: 'critical',
    actionRequired: 'Immediate break required — critical attention deficit detected',
    clinicalNote: 'Attention score critically low. Cognitive task performance severely compromised.',
  },
  {
    id: 'adhd-beta-low',
    name: 'Beta Power',
    condition: 'Beta < 7 µV²',
    category: 'adhd',
    severity: 'warning',
    actionRequired: 'Stimulate alertness — low beta indicates hypoarousal',
    clinicalNote: 'Low frontal beta power. Patient may be under-aroused. Consider alerting intervention.',
  },
];

// ─── Notification Item ────────────────────────────────────────────────────────
interface ActiveNotification {
  id: string;
  threshold: BiomarkerThreshold;
  value: string;
  triggeredAt: Date;
  acknowledged: boolean;
}

function evaluateThresholds(
  thetaAlphaRatio: number,
  interHemisphericCoherence: number,
  thetaPower: number,
  betaPower: number,
  deltaPower: number,
  attentionScore: number
): string[] {
  const triggered: string[] = [];
  const tbr = betaPower > 0 ? thetaPower / betaPower : thetaAlphaRatio;

  if (thetaAlphaRatio > 2.2) triggered.push('alz-tar-critical');
  else if (thetaAlphaRatio > 1.8) triggered.push('alz-tar-warning');

  if (interHemisphericCoherence < 52) triggered.push('alz-coherence-critical');
  else if (interHemisphericCoherence < 60) triggered.push('alz-coherence-warning');

  if (deltaPower > 22) triggered.push('alz-delta-critical');

  if (tbr > 2.4) triggered.push('adhd-tbr-critical');
  else if (tbr > 1.8) triggered.push('adhd-tbr-warning');

  if (attentionScore < 30) triggered.push('adhd-attention-critical');
  if (betaPower < 7) triggered.push('adhd-beta-low');

  return triggered;
}

function getMetricValue(
  thresholdId: string,
  thetaAlphaRatio: number,
  interHemisphericCoherence: number,
  thetaPower: number,
  betaPower: number,
  deltaPower: number,
  attentionScore: number
): string {
  const tbr = betaPower > 0 ? thetaPower / betaPower : thetaAlphaRatio;
  if (thresholdId.startsWith('alz-tar')) return `TAR = ${thetaAlphaRatio.toFixed(2)}`;
  if (thresholdId.startsWith('alz-coherence')) return `Coherence = ${interHemisphericCoherence.toFixed(0)}%`;
  if (thresholdId.startsWith('alz-delta')) return `Delta = ${deltaPower.toFixed(1)} µV²`;
  if (thresholdId.startsWith('adhd-tbr')) return `TBR = ${tbr.toFixed(2)}`;
  if (thresholdId.startsWith('adhd-attention')) return `Attention = ${attentionScore.toFixed(0)}`;
  if (thresholdId.startsWith('adhd-beta')) return `Beta = ${betaPower.toFixed(1)} µV²`;
  return '';
}

// ─── Notification Toast ───────────────────────────────────────────────────────
function NotificationToast({
  notification,
  onAcknowledge,
  onDismiss,
}: {
  notification: ActiveNotification;
  onAcknowledge: (id: string) => void;
  onDismiss: (id: string) => void;
}) {
  const isCritical = notification.threshold.severity === 'critical';
  const isAlzheimer = notification.threshold.category === 'alzheimer';

  const borderColor = isCritical ? '#ef4444' : '#f59e0b';
  const bgColor = isCritical ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.06)';
  const accentColor = isCritical ? '#ef4444' : '#f59e0b';
  const categoryColor = isAlzheimer ? '#a78bfa' : '#22d3ee';
  const CategoryIcon = isAlzheimer ? Brain : Activity;

  const elapsed = Math.floor((Date.now() - notification.triggeredAt.getTime()) / 1000);
  const timeLabel = elapsed < 60 ? `${elapsed}s ago` : `${Math.floor(elapsed / 60)}m ago`;

  return (
    <div
      className="relative rounded-xl overflow-hidden transition-all duration-300"
      style={{
        background: bgColor,
        border: `1px solid ${borderColor}`,
        boxShadow: isCritical ? `0 0 16px rgba(239,68,68,0.2), 0 4px 12px rgba(0,0,0,0.3)` : `0 4px 12px rgba(0,0,0,0.25)`,
      }}
    >
      {/* Critical pulse bar */}
      {isCritical && (
        <div
          className="absolute top-0 left-0 right-0 h-0.5"
          style={{
            background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
            animation: 'pulse 2s ease-in-out infinite',
          }}
        />
      )}

      <div className="p-3">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isCritical ? (
              <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' }}>
                <AlertTriangle size={12} style={{ color: '#ef4444' }} />
              </div>
            ) : (
              <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)' }}>
                <AlertTriangle size={12} style={{ color: '#f59e0b' }} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span
                  className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
                  style={{ background: `${categoryColor}15`, color: categoryColor, border: `1px solid ${categoryColor}30` }}
                >
                  <CategoryIcon size={7} className="inline mr-0.5" />
                  {notification.threshold.category === 'alzheimer' ? 'Alzheimer' : 'ADHD'}
                </span>
                <span
                  className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
                  style={{ background: `${accentColor}15`, color: accentColor, border: `1px solid ${accentColor}30` }}
                >
                  {isCritical ? '⚠ CRITICAL' : '⚡ WARNING'}
                </span>
              </div>
              <p className="text-[11px] font-bold text-white mt-0.5 leading-tight">
                {notification.threshold.name}
              </p>
              <p className="text-[10px] font-mono mt-0.5" style={{ color: accentColor }}>
                {notification.value}
              </p>
            </div>
          </div>
          <button
            onClick={() => onDismiss(notification.id)}
            className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <X size={10} style={{ color: '#4A7AB5' }} />
          </button>
        </div>

        {/* Action required */}
        <div className="rounded-lg p-2 mb-2"
          style={{ background: `${accentColor}10`, border: `1px solid ${accentColor}20` }}>
          <p className="text-[9px] font-bold uppercase tracking-wider mb-0.5" style={{ color: accentColor }}>
            Action Required
          </p>
          <p className="text-[10px] text-white leading-snug font-medium">
            {notification.threshold.actionRequired}
          </p>
        </div>

        {/* Clinical note */}
        <p className="text-[9px] text-[#4A7AB5] leading-snug mb-2">
          {notification.threshold.clinicalNote}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 text-[9px] text-[#4A7AB5]">
            <Clock size={8} />
            <span>{timeLabel}</span>
          </div>
          {!notification.acknowledged ? (
            <button
              onClick={() => onAcknowledge(notification.id)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wider transition-all hover:opacity-80"
              style={{ background: `${accentColor}20`, color: accentColor, border: `1px solid ${accentColor}40` }}
            >
              <CheckCircle size={8} />
              Acknowledge
            </button>
          ) : (
            <span className="flex items-center gap-1 text-[9px] font-bold" style={{ color: '#22d3ee' }}>
              <CheckCircle size={8} />
              Acknowledged
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function BiomarkerNotificationSystem() {
  const { metrics } = useSession();
  const {
    thetaAlphaRatio,
    interHemisphericCoherence,
    thetaPower,
    betaPower,
    deltaPower,
    attentionScore,
  } = metrics;

  const [notifications, setNotifications] = useState<ActiveNotification[]>([]);
  const [muted, setMuted] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [totalTriggered, setTotalTriggered] = useState(0);
  const prevTriggeredRef = useRef<Set<string>>(new Set());
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Play alert tone for critical notifications
  const playAlertTone = useCallback((isCritical: boolean) => {
    if (muted) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = isCritical ? 880 : 660;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (isCritical ? 0.6 : 0.3));
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + (isCritical ? 0.6 : 0.3));
    } catch {
      // Audio not available
    }
  }, [muted]);

  // Evaluate thresholds every 2 seconds
  useEffect(() => {
    const triggeredIds = evaluateThresholds(
      thetaAlphaRatio,
      interHemisphericCoherence,
      thetaPower,
      betaPower,
      deltaPower,
      attentionScore
    );

    const triggeredSet = new Set(triggeredIds);
    const prev = prevTriggeredRef.current;

    // Find newly triggered thresholds
    const newlyTriggered = triggeredIds.filter(id => !prev.has(id));

    if (newlyTriggered.length > 0) {
      const newNotifications: ActiveNotification[] = newlyTriggered.map(id => {
        const threshold = THRESHOLDS.find(t => t.id === id)!;
        return {
          id: `${id}-${Date.now()}`,
          threshold,
          value: getMetricValue(id, thetaAlphaRatio, interHemisphericCoherence, thetaPower, betaPower, deltaPower, attentionScore),
          triggeredAt: new Date(),
          acknowledged: false,
        };
      });

      setNotifications(prev => {
        // Keep max 8 notifications, newest first
        const combined = [...newNotifications, ...prev].slice(0, 8);
        return combined;
      });
      setTotalTriggered(prev => prev + newlyTriggered.length);

      // Play tone for highest severity
      const hasCritical = newlyTriggered.some(id => THRESHOLDS.find(t => t.id === id)?.severity === 'critical');
      playAlertTone(hasCritical);
    }

    // Remove notifications for thresholds that are no longer active (unless acknowledged)
    setNotifications(prev =>
      prev.filter(n => {
        const baseId = n.threshold.id;
        return triggeredSet.has(baseId) || n.acknowledged;
      })
    );

    prevTriggeredRef.current = triggeredSet;
  }, [thetaAlphaRatio, interHemisphericCoherence, thetaPower, betaPower, deltaPower, attentionScore, playAlertTone]);

  const handleAcknowledge = useCallback((notifId: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === notifId ? { ...n, acknowledged: true } : n)
    );
  }, []);

  const handleDismiss = useCallback((notifId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notifId));
  }, []);

  const handleClearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const criticalCount = notifications.filter(n => n.threshold.severity === 'critical' && !n.acknowledged).length;
  const warningCount = notifications.filter(n => n.threshold.severity === 'warning' && !n.acknowledged).length;
  const unacknowledgedCount = criticalCount + warningCount;

  if (notifications.length === 0 && totalTriggered === 0) {
    return (
      <div
        className="rounded-2xl p-3 flex items-center gap-3"
        style={{
          background: 'rgba(34,211,238,0.04)',
          border: '1px solid rgba(34,211,238,0.15)',
        }}
      >
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.25)' }}>
          <Bell size={14} style={{ color: '#22d3ee' }} />
        </div>
        <div>
          <p className="text-[11px] font-bold text-white">All Biomarkers Normal</p>
          <p className="text-[10px] text-[#4A7AB5]">No threshold alerts — monitoring active</p>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-[#22d3ee] animate-pulse" />
          <span className="text-[9px] text-[#22d3ee] font-semibold">LIVE</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'rgba(10,20,40,0.85)',
        border: criticalCount > 0
          ? '1px solid rgba(239,68,68,0.4)'
          : warningCount > 0
          ? '1px solid rgba(245,158,11,0.35)'
          : '1px solid rgba(34,211,238,0.2)',
        boxShadow: criticalCount > 0
          ? '0 0 24px rgba(239,68,68,0.12)'
          : undefined,
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
        style={{
          background: criticalCount > 0
            ? 'rgba(239,68,68,0.06)'
            : warningCount > 0
            ? 'rgba(245,158,11,0.05)'
            : 'rgba(34,211,238,0.04)',
          borderBottom: expanded ? '1px solid rgba(255,255,255,0.06)' : 'none',
        }}
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="relative w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{
              background: criticalCount > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.12)',
              border: `1px solid ${criticalCount > 0 ? 'rgba(239,68,68,0.4)' : 'rgba(245,158,11,0.35)'}`,
            }}
          >
            <Bell size={14} style={{ color: criticalCount > 0 ? '#ef4444' : '#f59e0b' }} />
            {unacknowledgedCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-black text-white"
                style={{ background: criticalCount > 0 ? '#ef4444' : '#f59e0b' }}
              >
                {unacknowledgedCount}
              </span>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-bold text-white">Biomarker Alerts</span>
              {criticalCount > 0 && (
                <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
                  style={{ background: 'rgba(239,68,68,0.2)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)' }}>
                  {criticalCount} CRITICAL
                </span>
              )}
              {warningCount > 0 && (
                <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
                  style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.35)' }}>
                  {warningCount} WARNING
                </span>
              )}
            </div>
            <p className="text-[9px] text-[#4A7AB5]">
              {totalTriggered} total triggered this session
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setMuted(m => !m); }}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
            title={muted ? 'Unmute alerts' : 'Mute alerts'}
          >
            {muted
              ? <BellOff size={12} style={{ color: '#4A7AB5' }} />
              : <Bell size={12} style={{ color: '#4A7AB5' }} />
            }
          </button>
          {notifications.length > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); handleClearAll(); }}
              className="text-[9px] font-semibold px-2 py-1 rounded-lg hover:bg-white/10 transition-colors"
              style={{ color: '#4A7AB5' }}
            >
              Clear all
            </button>
          )}
          {expanded
            ? <ChevronUp size={14} style={{ color: '#4A7AB5' }} />
            : <ChevronDown size={14} style={{ color: '#4A7AB5' }} />
          }
        </div>
      </div>

      {/* Notification list */}
      {expanded && (
        <div className="p-3 flex flex-col gap-2 max-h-[420px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-[11px] text-[#4A7AB5]">All alerts cleared</p>
            </div>
          ) : (
            notifications.map(notification => (
              <NotificationToast
                key={notification.id}
                notification={notification}
                onAcknowledge={handleAcknowledge}
                onDismiss={handleDismiss}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
